import { formatTaskDuration } from "@/lib/caseTasks";
import { SELF_TASKS_ROW, type CenterStatsRow, type CommercialStatsRow } from "@/lib/managerStats";
import { Panel, ageTone, formatPercent } from "./managerUi";

const th = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500";
const thNum = `${th} whitespace-nowrap text-right`;
const td = "px-3 py-2 text-sm text-gray-700";
const tdNum = `${td} whitespace-nowrap text-right tabular-nums`;

function LoadingRow({ cols }: { cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-3 py-6 text-center text-sm text-gray-400" role="status">
        Chargement…
      </td>
    </tr>
  );
}

export function CenterStatsTable({
  rows,
  loading,
  onSelectCenter,
}: {
  rows: CenterStatsRow[];
  loading: boolean;
  onSelectCenter: (center: string) => void;
}) {
  return (
    <Panel title="Par centre" subtitle="En cours : toutes dates · terminées et délai : sur la période">
      <table className="w-full">
        <thead className="border-b border-gray-100">
          <tr>
            <th className={th}>Centre</th>
            <th className={thNum}>En cours</th>
            <th className={thNum}>Terminées</th>
            <th className={thNum}>Délai médian</th>
            <th className={thNum}>Plus ancienne</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading ? (
            <LoadingRow cols={5} />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-400">
                Aucun centre.
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const empty = row.openCount === 0 && row.completedCount === 0;
              return (
                <tr key={row.key} className={empty ? "text-gray-400" : undefined}>
                  <td className={`${td} font-medium`}>
                    {row.key === SELF_TASKS_ROW ? (
                      <span className="italic text-gray-600">{row.label}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onSelectCenter(row.key)}
                        className={`text-left hover:text-indigo-700 hover:underline focus:outline-none focus-visible:underline ${empty ? "text-gray-400" : "text-gray-900"}`}
                        title="Filtrer sur ce centre"
                      >
                        {row.label}
                      </button>
                    )}
                  </td>
                  <td className={`${tdNum} ${row.openCount ? "font-semibold text-gray-900" : "text-gray-400"}`}>{row.openCount}</td>
                  <td className={`${tdNum} ${row.completedCount ? "" : "text-gray-400"}`}>{row.completedCount}</td>
                  <td className={tdNum}>{formatTaskDuration(row.medianCompletionMs)}</td>
                  <td className={`${tdNum} ${row.oldestOpenAgeMs == null ? "text-gray-400" : ageTone(row.oldestOpenAgeMs)}`}>
                    {formatTaskDuration(row.oldestOpenAgeMs)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </Panel>
  );
}

export function CommercialStatsTable({
  rows,
  loading,
  nameOf,
  onSelect,
}: {
  rows: CommercialStatsRow[];
  loading: boolean;
  nameOf: (profileId: string) => string;
  onSelect: (profileId: string) => void;
}) {
  return (
    <Panel title="Par commercial" subtitle="Tâches cochées sur la période · part des tâches d'équipe de ses centres">
      <table className="w-full table-fixed">
        <colgroup>
          <col />
          <col className="w-[76px]" />
          <col className="w-[104px]" />
          <col className="w-[190px]" />
        </colgroup>
        <thead className="border-b border-gray-100">
          <tr>
            <th className={th}>Commercial</th>
            <th className={thNum}>Cochées</th>
            <th className={thNum}>Délai médian</th>
            <th className={`${th} whitespace-nowrap`}>Part de ses centres</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading ? (
            <LoadingRow cols={4} />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-400">
                Aucun membre d'équipe ni tâche cochée.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.profileId}>
                <td className={`${td} min-w-0`}>
                  <button
                    type="button"
                    onClick={() => onSelect(row.profileId)}
                    className="text-left font-medium text-gray-900 hover:text-indigo-700 hover:underline focus:outline-none focus-visible:underline"
                    title="Voir les tâches cochées par cette personne"
                  >
                    {nameOf(row.profileId)}
                  </button>
                  <div className="truncate text-xs text-gray-400" title={row.centers.join(", ")}>
                    {row.centers.length ? row.centers.join(", ") : "Membre d'aucune équipe"}
                  </div>
                </td>
                <td className={`${tdNum} ${row.completedCount ? "font-semibold text-gray-900" : "text-gray-400"}`}>
                  {row.completedCount}
                </td>
                <td className={tdNum}>{formatTaskDuration(row.medianCompletionMs)}</td>
                <td className={td}>
                  {row.shareOfTeam == null ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <div className="flex items-center gap-2" title={`${Math.round(row.shareOfTeam * row.teamCompletedCount)} sur ${row.teamCompletedCount} tâches terminées dans ses centres`}>
                      <div className="h-1.5 min-w-[60px] flex-1 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.round(row.shareOfTeam * 100)}%` }} />
                      </div>
                      <span className="w-[68px] shrink-0 whitespace-nowrap text-right text-xs tabular-nums text-gray-600">
                        {formatPercent(row.shareOfTeam)} <span className="text-gray-400">/ {row.teamCompletedCount}</span>
                      </span>
                    </div>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Panel>
  );
}
